import { humanReadableBytes } from '@flakiness/shared/common/utils.js';
import { SlRadioGroup } from '@shoelace-style/shoelace';
import * as d3 from 'd3';
import { css, html, LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { linkStyles } from './cssstyles.js';

// Metrics arrive bucketed by calendar day, so days are compared as plain dates
// and only turned into `Date` at UTC midnight when they are handed to d3.
const TZ_UTC = 'UTC';

export type UsageBucketSize = 'day' | 'week' | 'month';

export type UsageChartRange = {
  since: Temporal.PlainDate,
  until: Temporal.PlainDate,
  // Set while the chart plots everything it was given, so callers can keep
  // their own wording for the full period instead of spelling out the dates.
  isFullPeriod: boolean,
};

export class FKUsageRangeEvent extends Event {
  static readonly eventName = 'fk-usage-range';
  readonly range: UsageChartRange;

  constructor(range: UsageChartRange) {
    super(FKUsageRangeEvent.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.range = range;
  }
}

type Bucket = {
  start: Temporal.PlainDate,
  end: Temporal.PlainDate,
  // `start` as a UTC midnight, which is what the d3 scales are keyed on.
  date: Date,
  value: number,
};

// Weeks start on Sunday to match the contribution calendar in <test-health-calendar>.
function bucketStart(date: Temporal.PlainDate, size: UsageBucketSize): Temporal.PlainDate {
  if (size === 'week')
    return date.subtract({ days: date.dayOfWeek % 7 });
  if (size === 'month')
    return date.with({ day: 1 });
  return date;
}

function bucketEnd(date: Temporal.PlainDate, size: UsageBucketSize): Temporal.PlainDate {
  if (size === 'week')
    return bucketStart(date, size).add({ days: 6 });
  if (size === 'month')
    return date.with({ day: date.daysInMonth });
  return date;
}

function nextBucketStart(start: Temporal.PlainDate, size: UsageBucketSize): Temporal.PlainDate {
  if (size === 'week')
    return start.add({ weeks: 1 });
  if (size === 'month')
    return start.add({ months: 1 });
  return start.add({ days: 1 });
}

function toDate(date: Temporal.PlainDate): Date {
  return new Date(date.toZonedDateTime(TZ_UTC).epochMilliseconds);
}

function toPlainDate(epochMilliseconds: number): Temporal.PlainDate {
  return Temporal.Instant.fromEpochMilliseconds(epochMilliseconds).toZonedDateTimeISO(TZ_UTC).toPlainDate();
}

function formatDay(date: Temporal.PlainDate): string {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
}

function formatDayYear(date: Temporal.PlainDate): string {
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const PADDING_TOP = 20;
const PADDING_RIGHT = 20;
const XAXIS_SIZE = 20;
const YAXIS_SIZE = 50;
// Height of the sparkline the brush sits on; the date axis lives below it.
const BRUSH_AREA_HEIGHT = 30;
const BRUSH_HANDLE_SIZE = 10;
// d3 hangs each handle half its own size past the end of the brushed area, and
// the stroke and shadow want a little more room still.
const BRUSH_HANDLE_OVERHANG = BRUSH_HANDLE_SIZE / 2 + 3;
// Headroom above the sparkline, so the overhang is not clipped by the svg edge.
const BRUSH_PADDING_TOP = BRUSH_HANDLE_OVERHANG;
// The date labels sit below the handles rather than running underneath them.
const CONTEXT_AXIS_TOP = BRUSH_PADDING_TOP + BRUSH_AREA_HEIGHT + BRUSH_HANDLE_SIZE / 2;
const CONTEXT_HEIGHT = CONTEXT_AXIS_TOP + XAXIS_SIZE;
// Rough width of one x-axis label, used to thin out ticks so they never collide.
const TICK_WIDTH = 52;
// A handful of buckets stretched across the full width reads as a block of
// colour rather than as bars, so bars stop growing past this and centre in
// their slot instead.
const MAX_BAR_WIDTH = 48;
// How much of the period a plain click on the strip zooms to, centred on the
// clicked day.
const CLICK_ZOOM_DAYS = 14;
// The period strip is a silhouette of the whole period rather than a second
// chart, so days are downsampled to roughly this many points before smoothing.
const MINIMAP_SAMPLES = 110;

@customElement('usage-chart')
export class UsageChart extends LitElement {

  @property({ attribute: false }) metrics: { day: string, value: number }[] = [];
  @property({ attribute: false }) unit: string = '';
  @property({ attribute: false }) since?: Temporal.PlainDate;
  @property({ attribute: false }) until?: Temporal.PlainDate;

  @query('#focus') private _focusSvg?: SVGElement;
  @query('#context') private _contextSvg?: SVGElement;

  @state() private _bucketSize: UsageBucketSize = 'day';
  // The brushed sub-range, in day precision and before bucket snapping.
  // Undefined means the whole period is shown.
  @state() private _selection?: { since: Temporal.PlainDate, until: Temporal.PlainDate };
  @state() private _hoveredBucket?: Bucket;

  private _buckets: Bucket[] = [];
  private _xScale = d3.scaleBand<Date>();
  private _yScale = d3.scaleLinear<number>();
  private _contextScale = d3.scaleUtc();
  // Left and right edge of the strip's plot area, cached so that a drag can
  // repaint the scrim without re-measuring.
  private _contextExtent?: [number, number];
  private _brush?: d3.BrushBehavior<unknown>;
  // Chart updates keep running while a handle is dragged, but the brush itself
  // must not be re-applied underneath the pointer.
  private _isBrushing = false;
  private _lastEmittedRange?: string;
  // The period the current zoom was picked out of, so that a new one can be
  // told apart from the same one arriving as fresh objects.
  private _selectionPeriod?: string;
  private _resizeObserver?: ResizeObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this._resizeObserver = new ResizeObserver(() => this._updateCharts());
    this._resizeObserver.observe(this);
  }

  override disconnectedCallback(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
    super.disconnectedCallback();
  }

  /**
   * Drops the zoom when the charted period itself changes, which happens when
   * the page is reused for another project or the retention window moves. The
   * zoom is a pair of dates from the old period: left in place it charts as
   * empty buckets, reports a stale range and total to the caller, and hands the
   * strip a window outside its own domain.
   */
  private _resetSelectionOnNewPeriod() {
    const period = this.since && this.until ? `${this.since}/${this.until}` : undefined;
    if (period === this._selectionPeriod)
      return;
    this._selectionPeriod = period;
    this._selection = undefined;
  }

  protected override willUpdate(changedProperties: PropertyValues): void {
    if (changedProperties.has('since') || changedProperties.has('until'))
      this._resetSelectionOnNewPeriod();
    if (changedProperties.has('metrics') || changedProperties.has('since') || changedProperties.has('until')
        || changedProperties.has('_bucketSize') || changedProperties.has('_selection')) {
      this._buckets = this._computeBuckets();
      this._hoveredBucket = undefined;
    }
  }

  protected override updated(changedProperties: PropertyValues): void {
    this._updateFocusChart();
    // Hovering a bar repaints the focus chart only; rebuilding the period strip
    // for it would redraw the whole sparkline on every mouse move.
    if (changedProperties.size !== 1 || !changedProperties.has('_hoveredBucket'))
      this._updateContextChart();
    this._maybeEmitRange();
  }

  /**
   * The range actually plotted: the brushed range (or the whole period) grown
   * outwards to whole buckets, so every bar covers the same number of days and
   * bar heights stay comparable.
   */
  private _visibleRange(): { since: Temporal.PlainDate, until: Temporal.PlainDate } | undefined {
    if (!this.since || !this.until)
      return undefined;
    const selection = this._selection ?? { since: this.since, until: this.until };
    return {
      since: bucketStart(selection.since, this._bucketSize),
      until: bucketEnd(selection.until, this._bucketSize),
    };
  }

  private _computeBuckets(): Bucket[] {
    const range = this._visibleRange();
    if (!range)
      return [];

    const dayToValue = new Map((this.metrics ?? []).map(metric => [metric.day, metric.value]));
    const buckets: Bucket[] = [];
    for (let start = range.since; Temporal.PlainDate.compare(start, range.until) <= 0; start = nextBucketStart(start, this._bucketSize)) {
      const end = bucketEnd(start, this._bucketSize);
      let value = 0;
      for (let day = start; Temporal.PlainDate.compare(day, end) <= 0; day = day.add({ days: 1 }))
        value += dayToValue.get(day.toString()) ?? 0;
      buckets.push({ start, end, date: toDate(start), value });
    }
    return buckets;
  }

  private _maybeEmitRange() {
    const range = this._visibleRange();
    if (!range)
      return;
    const key = [range.since.toString(), range.until.toString(), this._selection ? 'partial' : 'full'].join(':');
    if (key === this._lastEmittedRange)
      return;
    this._lastEmittedRange = key;
    this.dispatchEvent(new FKUsageRangeEvent({ ...range, isFullPeriod: !this._selection }));
  }

  private _bucketLabel(bucket: Bucket, long: boolean): string {
    if (this._bucketSize === 'month') {
      if (long)
        return bucket.start.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      return bucket.start.month === 1
        ? bucket.start.toLocaleString('en-US', { month: 'short', year: 'numeric' })
        : bucket.start.toLocaleString('en-US', { month: 'short' });
    }
    if (this._bucketSize === 'week') {
      if (!long)
        return formatDay(bucket.start);
      return bucket.start.month === bucket.end.month && bucket.start.year === bucket.end.year
        ? `${formatDay(bucket.start)} – ${bucket.end.day}, ${bucket.end.year}`
        : `${formatDay(bucket.start)} – ${formatDayYear(bucket.end)}`;
    }
    return long ? formatDayYear(bucket.start) : formatDay(bucket.start);
  }

  override render() {
    return html`
      <div class=toolbar>
        <div class=title><slot></slot></div>
        <sl-radio-group
          size=small
          value=${this._bucketSize}
          @sl-change=${(event: Event) => this._bucketSize = (event.target as SlRadioGroup).value as UsageBucketSize}
        >
          <sl-radio-button value=day>Day</sl-radio-button>
          <sl-radio-button value=week>Week</sl-radio-button>
          <sl-radio-button value=month>Month</sl-radio-button>
        </sl-radio-group>
      </div>
      <svg id=focus @mousemove=${this._onMouseMove} @mouseout=${this._onMouseOut}>
        <g id=timeseries></g>
        <g id=xaxis></g>
        <g id=yaxis></g>
      </svg>
      <svg id=context>
        <g id=strip transform="translate(0, ${BRUSH_PADDING_TOP})">
          <path id=sparkline></path>
          <rect class=scrim id=scrim-before></rect>
          <rect class=scrim id=scrim-after></rect>
          <g id=brush></g>
        </g>
        <g id=context-axis></g>
      </svg>
    `;
  }

  private _onMouseMove(event: MouseEvent) {
    if (!this._buckets.length)
      return;
    const [xFrom, xTo] = this._xScale.range();
    const [yTo, yFrom] = this._yScale.range();

    const [mx, my] = d3.pointer(event);
    if (mx < xFrom || xTo < mx || my < yFrom || yTo < my) {
      this._hoveredBucket = undefined;
      return;
    }

    // Compute distance from a bar to a point.
    const distance = (bucket: Bucket) => {
      const x1 = this._xScale(bucket.date)!;
      const x2 = x1 + this._xScale.bandwidth();
      if (mx < x1)
        return x1 - mx;
      if (mx > x2)
        return mx - x2;
      return 0;
    }
    this._hoveredBucket = this._buckets.reduce((a, b) => distance(a) < distance(b) ? a : b);
  }

  private _onMouseOut(event: MouseEvent) {
    this._hoveredBucket = undefined;
  }

  private _formatValue(d: number) {
    if (this.unit === 'bytes')
      return humanReadableBytes(d);
    return Number.isInteger(d) ? (d < 1000 ? d + '' : d3.format('.2s')(d)) : ''
  }

  private _updateCharts() {
    this._updateFocusChart();
    this._updateContextChart();
  }

  private _updateFocusChart() {
    const buckets = this._buckets;
    const chart = this._focusSvg;
    const chartBoundingBox = chart?.getBoundingClientRect();

    if (!buckets.length || !chartBoundingBox || !chartBoundingBox.width || !chartBoundingBox.height || !chart)
      return;

    const WIDTH = chartBoundingBox.width;
    const HEIGHT = chartBoundingBox.height;

    const xScale = this._xScale
      .domain(buckets.map(bucket => bucket.date))
      .range([YAXIS_SIZE, WIDTH - PADDING_RIGHT])
      .padding(0.05);

    const bucketByTime = new Map(buckets.map(bucket => [+bucket.date, bucket]));
    const maxLabels = Math.max(1, Math.floor((WIDTH - YAXIS_SIZE - PADDING_RIGHT) / TICK_WIDTH));
    const period = Math.ceil(buckets.length / maxLabels);

    const xAxis = d3.axisBottom<Date>(xScale)
      .tickFormat(d => this._bucketLabel(bucketByTime.get(+d)!, false))
      .tickValues(buckets.filter((bucket, index) => index % period === 0).map(bucket => bucket.date))
      .tickSizeOuter(0);

    const yExtent = d3.extent(buckets.map(bucket => bucket.value)) as [number, number];
    const yMaxExtent = Math.abs(yExtent[1]) < 1e-9 ? 1 : Math.ceil(yExtent[1]*100 * 1.3)/100;
    const yScale = this._yScale
      .domain([0, yMaxExtent])
      .range([HEIGHT - XAXIS_SIZE, PADDING_TOP]);
    const yAxis = d3.axisLeft<number>(yScale);
    yAxis.tickFormat(this._formatValue.bind(this));

    if (this._hoveredBucket) {
      xAxis.tickValues([this._hoveredBucket.date]);
      xAxis.tickFormat(d => this._bucketLabel(bucketByTime.get(+d)!, true));
      yAxis.tickValues([this._hoveredBucket.value]);
    }

    d3.select(chart)
      .select<SVGGElement>('#xaxis')
      .call(xAxis)
      .attr('transform', `translate(0, ${HEIGHT - XAXIS_SIZE})`);

    d3.select(chart)
      .select<SVGGElement>('#yaxis')
      .call(yAxis)
      .attr('transform', `translate(${YAXIS_SIZE}, 0)`);

    const barWidth = Math.min(xScale.bandwidth(), MAX_BAR_WIDTH);
    const barOffset = (xScale.bandwidth() - barWidth) / 2;

    d3.select(chart)
      .select('#timeseries')
      .selectAll<SVGRectElement, Bucket>('rect')
        .data(buckets, bucket => bucket.start.toString())
        .join('rect')
        .attr("x", bucket => xScale(bucket.date)! + barOffset)
        .attr("width", barWidth)
        .attr("height", bucket => bucket.value === 0 ? 0 : Math.max(5, HEIGHT - XAXIS_SIZE - yScale(bucket.value)))
        .attr("y", bucket => bucket.value === 0 ? yScale(0) : Math.min(yScale(bucket.value), HEIGHT - XAXIS_SIZE - 5))
        .attr('class', bucket => {
          if (!this._hoveredBucket)
            return '';
          return (bucket === this._hoveredBucket) ? '' : 'dimmed';
        })
  }

  /**
   * The whole period downsampled to a fixed number of points. The strip is a
   * silhouette used to aim the brush, so it deliberately ignores the bucket
   * size and never changes when the chart switches between day, week and month.
   * Each sample takes the peak of its days, which keeps busy spells findable
   * once the curve smooths everything out.
   */
  private _minimapSeries(): { date: Date, value: number }[] {
    if (!this.since || !this.until)
      return [];

    const dayToValue = new Map((this.metrics ?? []).map(metric => [metric.day, metric.value]));
    const values: number[] = [];
    const days: Temporal.PlainDate[] = [];
    for (let day = this.since; Temporal.PlainDate.compare(day, this.until) <= 0; day = day.add({ days: 1 })) {
      days.push(day);
      values.push(dayToValue.get(day.toString()) ?? 0);
    }
    if (!days.length)
      return [];

    const binSize = Math.max(1, Math.ceil(days.length / MINIMAP_SAMPLES));
    const series: { date: Date, value: number }[] = [];
    for (let index = 0; index < days.length; index += binSize) {
      const bin = values.slice(index, index + binSize);
      series.push({ date: toDate(days[index]), value: Math.max(...bin) });
    }
    // Carry the last sample one day out so the area closes on the right edge.
    series.push({ date: toDate(this.until.add({ days: 1 })), value: series[series.length - 1].value });
    return series;
  }

  /**
   * Where the brush window sits, in pixels. This follows the raw brushed range
   * rather than the bucket-snapped one, so switching between day, week and
   * month leaves the strip untouched and only redraws the chart above it.
   */
  private _selectionPixels(): [number, number] | undefined {
    if (!this._contextExtent)
      return undefined;
    const [left, right] = this._contextExtent;
    if (!this._selection)
      return [left, right];
    const clamp = (x: number) => Math.min(right, Math.max(left, x));
    const x0 = clamp(this._contextScale(toDate(this._selection.since)));
    const x1 = clamp(this._contextScale(toDate(this._selection.until.add({ days: 1 }))));
    // A window that has landed outside the strip's domain would draw as a
    // negative-width rectangle, so show the whole period instead.
    return x1 > x0 ? [x0, x1] : [left, right];
  }

  // Mutes the parts of the strip that fall outside the brush window. Kept apart
  // from the rest of the strip so a drag can repaint it on every frame.
  private _updateScrim(pixels: [number, number] | null) {
    const chart = this._contextSvg;
    if (!chart || !this._contextExtent)
      return;
    const [left, right] = this._contextExtent;
    const [x0, x1] = pixels ?? [left, right];
    d3.select(chart).select('#scrim-before')
      .attr('x', left).attr('y', 0).attr('height', BRUSH_AREA_HEIGHT)
      .attr('width', Math.max(0, x0 - left));
    d3.select(chart).select('#scrim-after')
      .attr('x', x1).attr('y', 0).attr('height', BRUSH_AREA_HEIGHT)
      .attr('width', Math.max(0, right - x1));
  }

  private _updateContextChart() {
    // Nothing on the strip depends on the selection, so a drag only has to move
    // the brush itself, and re-applying it here would yank the handle out from
    // under the pointer.
    if (this._isBrushing)
      return;

    const chart = this._contextSvg;
    const chartBoundingBox = chart?.getBoundingClientRect();
    if (!chart || !chartBoundingBox?.width || !this.since || !this.until)
      return;

    const left = YAXIS_SIZE;
    const right = Math.max(left + 1, chartBoundingBox.width - PADDING_RIGHT);
    this._contextExtent = [left, right];

    // The strip spans the whole period, one day past `until` so that the last
    // day occupies a full slot rather than collapsing onto the right edge.
    const xScale = this._contextScale
      .domain([toDate(this.since), toDate(this.until.add({ days: 1 }))])
      .range([left, right]);

    const series = this._minimapSeries();
    const maxValue = d3.max(series, point => point.value) ?? 0;
    const yScale = d3.scaleLinear()
      .domain([0, maxValue > 0 ? maxValue : 1])
      .range([BRUSH_AREA_HEIGHT, 1]);

    const area = d3.area<{ date: Date, value: number }>()
      .x(point => xScale(point.date))
      .y0(BRUSH_AREA_HEIGHT)
      .y1(point => yScale(point.value))
      .curve(d3.curveMonotoneX);

    d3.select(chart).select('#sparkline').attr('d', area(series) ?? '');

    // Bare labels, no rule or tick marks: the sparkline's own flat base already
    // reads as the baseline, and a rule here would cut across the handles.
    const contextAxis = d3.axisBottom(xScale)
      .ticks(Math.max(2, Math.floor((right - left) / 100)))
      .tickSize(0)
      .tickPadding(4);
    d3.select(chart)
      .select<SVGGElement>('#context-axis')
      .call(contextAxis)
      .attr('transform', `translate(0, ${CONTEXT_AXIS_TOP})`);

    if (!this._brush) {
      this._brush = d3.brushX()
        .handleSize(BRUSH_HANDLE_SIZE)
        .on('start', (event: d3.D3BrushEvent<unknown>) => {
          if (event.sourceEvent)
            this._isBrushing = true;
        })
        .on('brush', (event: d3.D3BrushEvent<unknown>) => this._onBrush(event))
        .on('end', (event: d3.D3BrushEvent<unknown>) => this._onBrushEnd(event));
    }
    this._brush.extent([[left, 0], [right, BRUSH_AREA_HEIGHT]]);

    const brush = d3.select(chart).select<SVGGElement>('#brush');
    brush.call(this._brush);
    // The window is always drawn, spanning everything when nothing is brushed,
    // so the handles stay on screen as an invitation to drag them. A window
    // covering everything has nothing to pan, and it would otherwise swallow
    // the drag that picks out a new one, so it lets the press through instead.
    brush.classed('full', !this._selection);
    const pixels = this._selectionPixels();
    if (pixels)
      brush.call(this._brush.move, pixels);
    this._updateScrim(pixels ?? null);
  }

  private _onBrush(event: d3.D3BrushEvent<unknown>) {
    // Programmatic `brush.move` calls come back through here with no source event.
    if (!event.sourceEvent)
      return;
    this._selection = this._selectionFromPixels(event.selection as [number, number] | null);
    this._updateScrim(event.selection as [number, number] | null);
  }

  private _onBrushEnd(event: d3.D3BrushEvent<unknown>) {
    this._isBrushing = false;
    if (!event.sourceEvent)
      return;
    const pixels = event.selection as [number, number] | null;
    // d3 hands back an empty selection for a press that never moved. Landing on
    // a couple of weeks around that spot beats its default of clearing, which
    // did nothing at all while the window already covered the whole period.
    this._selection = pixels ? this._selectionFromPixels(pixels) : this._selectionAroundClick(event.sourceEvent);
    // Even a real drag leaves the window a fraction of a pixel off the day it
    // resolved to, so redraw either way.
    this.requestUpdate();
  }

  private _selectionAroundClick(event: Event): { since: Temporal.PlainDate, until: Temporal.PlainDate } | undefined {
    if (!this.since || !this.until || !this._contextSvg || !this._contextExtent)
      return undefined;

    const [left, right] = this._contextExtent;
    const [x] = d3.pointer(event, this._contextSvg);
    if (x < left || x > right)
      return undefined;

    const clicked = toPlainDate(+this._contextScale.invert(x));
    let since = clicked.subtract({ days: Math.floor(CLICK_ZOOM_DAYS / 2) });
    let until = since.add({ days: CLICK_ZOOM_DAYS - 1 });
    // Clicking near either end slides the window back inside the period rather
    // than letting it shrink.
    if (Temporal.PlainDate.compare(until, this.until) > 0) {
      until = this.until;
      since = until.subtract({ days: CLICK_ZOOM_DAYS - 1 });
    }
    if (Temporal.PlainDate.compare(since, this.since) < 0) {
      since = this.since;
      until = since.add({ days: CLICK_ZOOM_DAYS - 1 });
    }
    if (Temporal.PlainDate.compare(until, this.until) > 0)
      until = this.until;
    // The period is no longer than the window, so there is nothing to zoom into.
    if (Temporal.PlainDate.compare(since, this.since) <= 0 && Temporal.PlainDate.compare(until, this.until) >= 0)
      return undefined;
    return { since, until };
  }

  private _selectionFromPixels(pixels: [number, number] | null): { since: Temporal.PlainDate, until: Temporal.PlainDate } | undefined {
    if (!pixels || !this.since || !this.until)
      return undefined;

    let since = toPlainDate(+this._contextScale.invert(pixels[0]));
    // The right edge sits on the start of the first excluded day, so step back
    // a millisecond to land on the last day that is actually selected.
    let until = toPlainDate(+this._contextScale.invert(pixels[1]) - 1);
    if (Temporal.PlainDate.compare(since, this.since) < 0)
      since = this.since;
    if (Temporal.PlainDate.compare(until, this.until) > 0)
      until = this.until;
    if (Temporal.PlainDate.compare(since, until) > 0)
      until = since;
    // A selection covering everything is the same as having no selection at all.
    if (Temporal.PlainDate.compare(since, this.since) <= 0 && Temporal.PlainDate.compare(until, this.until) >= 0)
      return undefined;
    return { since, until };
  }

  static styles = [linkStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      flex: auto;
      min-width: 0;
    }

    .toolbar {
      display: flex;
      flex: none;
      align-items: center;
      gap: var(--sl-spacing-small);
    }

    /* Held to one line: the header restates the brushed range, so letting it
       wrap would take a line off the chart on every scrub. */
    .title {
      flex: auto;
      min-width: 0;
      font-weight: var(--sl-font-weight-bold);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    #timeseries rect {
      rx: 2;
      ry: 2;
      fill: steelblue;
    }

    #timeseries rect.dimmed {
      fill: var(--sl-color-neutral-300);
    }

    #xaxis, #yaxis, #context-axis {
      user-select: none;
    }

    #focus {
      flex: auto;
      min-height: 0;
      width: 100%;
      position: relative;
    }

    #context {
      flex: none;
      width: 100%;
      height: ${CONTEXT_HEIGHT}px;
    }

    #sparkline {
      fill: var(--sl-color-neutral-300);
    }

    .scrim {
      fill: var(--sl-color-neutral-0);
      fill-opacity: 0.65;
      pointer-events: none;
    }

    #context-axis {
      font-size: var(--sl-font-size-x-small);
      color: var(--sl-color-neutral-500);
    }

    #context-axis .domain {
      display: none;
    }

    /* The window itself stays clear -- the scrim mutes everything outside it --
       but it keeps a faint fill so that it can still be grabbed and dragged. */
    #brush .selection {
      rx: 0;
      ry: 0;
      fill: var(--sl-color-primary-500);
      fill-opacity: 0.06;
      stroke: var(--sl-color-primary-500);
      stroke-opacity: 0.4;
    }

    /* A window covering everything is not a zoom, so it shows as a plain frame
       and only picks up a tint once it actually narrows the chart. */
    #brush.full .selection {
      fill-opacity: 0;
      pointer-events: none;
    }

    #brush .handle {
      rx: 3;
      ry: 3;
      fill: var(--sl-color-neutral-0);
      stroke: var(--sl-color-neutral-500);
      filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.2));
    }

    #brush .handle:hover {
      fill: var(--sl-color-primary-50);
      stroke: var(--sl-color-primary-600);
    }
  `];
}
